// SavWar Arşiv - Geçmiş Seanslar
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../lib/api';

// Türkçe karakterleri normalize et (backend ile uyumlu)
const normalizeSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9_-]/g, ''); // Sadece a-z, 0-9, _, - karakterleri bırak
};

interface Topic {
  _id: string;
  tag: string;
  slug: string;
}

interface Session {
  _id: string;
  topicId: Topic;
  startAt: string;
  endAt: string;
  active: boolean;
  stats: {
    posts: number;
    stars: number;
    commenters: number;
  };
}

export default function SavWarArchivePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async (cursor?: string) => {
    try {
      if (!cursor) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      const url = cursor 
        ? `/api/savwar/sessions?limit=20&cursor=${encodeURIComponent(cursor)}`
        : `/api/savwar/sessions?limit=20`;
      
      const response: any = await apiGet(url);
      
      if (response && response.success && response.data) {
        if (cursor) {
          setSessions(prev => [...prev, ...response.data.sessions]);
        } else {
          setSessions(response.data.sessions);
        }
        setNextCursor(response.data.nextCursor);
      }
    } catch (err: any) {
      console.error('[SavWarArchive] Load error:', err);
      setError('Arşiv yüklenirken hata oluştu');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (nextCursor && !loadingMore) {
      loadSessions(nextCursor);
    }
  };

  const handleSessionClick = (session: Session) => {
    // Mobile ile aynı: Topic sayfasına git
    const slug = normalizeSlug(session.topicId.tag.replace('#', ''));
    navigate(`/savwar/topics/${slug}`);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Bugün';
    if (diffDays === 1) return 'Dün';
    if (diffDays < 7) return `${diffDays} gün önce`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} hafta önce`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} ay önce`;
    return date.toLocaleDateString('tr-TR');
  };

  // Süre hesaplama (mobile ile aynı)
  const calculateDuration = (startAt: string, endAt: string) => {
    const start = new Date(startAt);
    const end = new Date(endAt);
    const diffMs = end.getTime() - start.getTime();
    const hours = Math.round(diffMs / (1000 * 60 * 60));
    return `${hours}h`;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#000',
        color: '#888',
      }}>
        <div style={{ fontSize: 24, marginBottom: 12 }}>⏳</div>
        <div>Arşiv yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, backgroundColor: '#000', minHeight: '100vh' }}>
        <div style={{ color: 'tomato', marginBottom: 16 }}>{error}</div>
        <button onClick={() => loadSessions()}>Tekrar Dene</button>
      </div>
    );
  }

  return (
    <div style={{ 
      backgroundColor: '#000', 
      minHeight: '100vh',
      paddingBottom: 40,
    }}>
      {/* Header - Mobile ile aynı */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        borderBottom: '1px solid #1a1a1a',
        backgroundColor: '#000',
      }}>
        <button
          onClick={() => navigate('/savwar')}
          style={{
            padding: 8,
            minWidth: 60,
            background: 'none',
            border: 'none',
            color: '#FFD700',
            fontSize: 16,
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          ← Geri
        </button>
        <h2 style={{
          fontSize: 20,
          fontWeight: '700',
          color: '#FFD700',
          margin: 0,
        }}>
          SavWar Arşivi
        </h2>
        <div style={{ width: 60 }} />
      </div>

      {/* Sessions List */}
      <div style={{ padding: 16 }}>
        {sessions.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 60,
            paddingBottom: 60,
          }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>📚</div>
            <div style={{
              fontSize: 18,
              fontWeight: '700',
              color: '#FFD700',
              marginBottom: 8,
            }}>
              Henüz Arşivlenmiş Oturum Yok
            </div>
            <div style={{
              fontSize: 14,
              color: '#888',
              textAlign: 'center',
            }}>
              Tamamlanan SavWar oturumları burada görünecek
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {sessions.map((session) => {
                const duration = calculateDuration(session.startAt, session.endAt);
                const dateStr = formatDate(session.startAt);

                return (
                  <div
                    key={session._id}
                    onClick={() => handleSessionClick(session)}
                    style={{
                      backgroundColor: '#1a1a1a',
                      borderRadius: 12,
                      padding: 16,
                      border: '1px solid #2a2a2a',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#222')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#1a1a1a')}
                  >
                    {/* Header: Topic + Duration */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 8,
                    }}>
                      <div style={{
                        fontSize: 20,
                        fontWeight: '700',
                        color: '#FFD700',
                      }}>
                        {session.topicId.tag}
                      </div>
                      <div style={{
                        backgroundColor: '#114232',
                        paddingLeft: 12,
                        paddingRight: 12,
                        paddingTop: 4,
                        paddingBottom: 4,
                        borderRadius: 12,
                      }}>
                        <div style={{
                          color: '#FFD700',
                          fontSize: 12,
                          fontWeight: '600',
                        }}>
                          {duration}
                        </div>
                      </div>
                    </div>

                    {/* Date */}
                    <div style={{
                      fontSize: 14,
                      color: '#888',
                      marginBottom: 12,
                    }}>
                      {dateStr}
                    </div>

                    {/* Stats */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-around',
                      paddingTop: 12,
                      paddingBottom: 12,
                      borderTop: '1px solid #2a2a2a',
                      borderBottom: '1px solid #2a2a2a',
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: 12,
                          color: '#888',
                          marginBottom: 4,
                        }}>
                          Gönderiler
                        </div>
                        <div style={{
                          fontSize: 18,
                          fontWeight: '700',
                          color: '#FFD700',
                        }}>
                          {session.stats.posts}
                        </div>
                      </div>

                      <div style={{
                        width: 1,
                        height: 30,
                        backgroundColor: '#2a2a2a',
                      }} />

                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: 12,
                          color: '#888',
                          marginBottom: 4,
                        }}>
                          Yıldızlar
                        </div>
                        <div style={{
                          fontSize: 18,
                          fontWeight: '700',
                          color: '#FFD700',
                        }}>
                          {session.stats.stars}
                        </div>
                      </div>

                      <div style={{
                        width: 1,
                        height: 30,
                        backgroundColor: '#2a2a2a',
                      }} />

                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: 12,
                          color: '#888',
                          marginBottom: 4,
                        }}>
                          Yorumcular
                        </div>
                        <div style={{
                          fontSize: 18,
                          fontWeight: '700',
                          color: '#FFD700',
                        }}>
                          {session.stats.commenters}
                        </div>
                      </div>
                    </div>

                    {/* Archived Badge */}
                    <div style={{
                      marginTop: 12,
                      textAlign: 'center',
                    }}>
                      <div style={{
                        fontSize: 12,
                        color: '#888',
                        fontWeight: '600',
                      }}>
                        📚 Arşivlendi
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {nextCursor && (
              <div style={{
                paddingTop: 20,
                textAlign: 'center',
              }}>
                {loadingMore ? (
                  <div style={{ color: '#888' }}>⏳ Yükleniyor...</div>
                ) : (
                  <button
                    onClick={handleLoadMore}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #2a2a2a',
                      borderRadius: 8,
                      color: '#FFD700',
                      fontSize: 14,
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Daha Fazla Yükle
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
